import { useState } from "react";

import { isDigitalSignatureField } from "@smart/bridge-types-basic";
import { Loading } from "@smart/itops-sb-design-system-dom";

import { useOrderedSections, Visibility } from "../../hooks";
import {
  AutoFillStatus,
  FieldItem,
  GroupItem,
  ResponseSubmitter,
  SectionItem,
  SubmissionItem,
  TeamItem,
  Toast,
} from "../../types";
import { IntakeSummary } from "../summary";

export type IntakeFormSummaryProps = {
  sections: SectionItem[];
  groups: GroupItem[];
  fields: FieldItem[];
  onSelect: (section: SectionItem) => void;
  submitResponses: ResponseSubmitter;
  completeFormOrRedirect: (
    status: SubmissionItem["status"],
    signingUrl?: string,
  ) => void;
  beginDigitalSignatureWait: () => void;
  attemptDigitalSignatureRecovery: () => Promise<void>;
  team: TeamItem;
  sidebar: boolean;
  setSidebar: (sidebar: boolean) => void;
  responses: Record<string, any>;
  isLoadingDBResponses: boolean;
  visibility: Visibility;
  disabled?: boolean;
  previewOptions?: {
    previewResponses: Record<string, any>;
    clearLastPreviewSection: () => void;
    previewErrorToggle?: [
      boolean,
      React.Dispatch<React.SetStateAction<boolean>>,
    ];
  };
  autoFillingStatus?: AutoFillStatus;
  isPreview?: boolean;
  isTeamHidden?: boolean;
  setToasts?: (toasts: Toast[]) => void;
  selectedLanguage?: string;
};

export const IntakeFormSummary = ({
  sections,
  groups,
  fields,
  onSelect,
  submitResponses,
  completeFormOrRedirect,
  beginDigitalSignatureWait,
  attemptDigitalSignatureRecovery,
  team,
  sidebar,
  setSidebar,
  responses,
  isLoadingDBResponses,
  visibility,
  disabled,
  previewOptions,
  autoFillingStatus,
  isPreview,
  isTeamHidden,
  setToasts,
  selectedLanguage,
}: IntakeFormSummaryProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const orderedSections = useOrderedSections({
    sections,
    groups,
    fields,
    visibility,
    responses,
  });
  const hasDigitalSignature = fields.some(
    (f) => isDigitalSignatureField(f) && visibility.visibleFields[f.uri],
  );
  const onSubmit = () => {
    if (previewOptions) previewOptions.clearLastPreviewSection();
    const status = "completed";
    setIsSubmitting(true);
    if (hasDigitalSignature) beginDigitalSignatureWait();
    submitResponses({ status, responses: {} })
      .then((result) => {
        setIsSubmitting(false);
        if (result?.error || !result) {
          if (hasDigitalSignature) {
            attemptDigitalSignatureRecovery().catch(console.error);
          } else {
            setToasts?.([
              {
                text:
                  result?.error ||
                  "Something went wrong submitting the form. Please try again.",
                palette: "destructive",
              },
            ]);
          }
        } else if (result?.operationIds)
          completeFormOrRedirect(status, result?.digitalSignature?.signingUrl);
      })
      .catch(() => {
        setIsSubmitting(false);
        if (hasDigitalSignature) {
          attemptDigitalSignatureRecovery().catch(console.error);
        }
      });
  };

  return (
    <>
      {isSubmitting && <Loading />}
      <IntakeSummary
        isForm={!disabled}
        hasDigitalSignature={hasDigitalSignature}
        team={team}
        onSelect={onSelect}
        onSubmit={onSubmit}
        onSubmitButton={disabled ? onSubmit : undefined}
        visibleSections={orderedSections}
        sidebar={sidebar}
        setSidebar={setSidebar}
        responses={previewOptions?.previewResponses || responses}
        isLoadingDBResponses={isLoadingDBResponses}
        previewErrorToggle={previewOptions?.previewErrorToggle}
        autoFillingStatus={autoFillingStatus}
        isPreview={isPreview}
        isTeamHidden={isTeamHidden}
        setToasts={setToasts}
        selectedLanguage={selectedLanguage}
      />
    </>
  );
};
